import * as React from 'react'
import { useCallback } from 'react'

import {
  AttendanceSidepanelCompensationBlockFragment,
  BulkCreateAttendanceTimeBlockCompensationMutation,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { getDateRange } from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/lib/getDateRange'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import useLegalEntities from 'modules/attendance/hooks/useLegalEntities'
import TimeBlocksSupplementForm from 'modules/attendance/lib/forms/TimeBlocksSupplementForm'
import { TimeBlocksBalanceCollection } from 'modules/attendance/store/models/TimeBlocksBalanceCollection'
import { UniqueTimeBlockData } from 'modules/attendance/store/models/UniqueTimeBlockData'
import { useFetchContractsTaxonomies } from 'modules/contracts/hooks/useFetchContractsTaxonomies'
import { useFetchCustomTimeRangeCategorySupplementMappings } from 'modules/payroll_bundle/hooks/useFetchReimbursableSupplementMappings'

import PayrollPeriod from './PayrollPeriod'
import PayrollSupplement from './PayrollSupplement'
import TimeBlocks from './TimeBlocks'

type AttendanceManualCompensation = NonNullable<
  NonNullable<
    BulkCreateAttendanceTimeBlockCompensationMutation['attendanceMutations']['bulkCreateTimeBlockSupplementAttendanceManualCompensation']
  >['manualCompensation']
>

interface Props {
  employeeId: string
  getResponseData: (response: AttendanceManualCompensation | undefined | null) => void
  loading: boolean
  onCancel: () => void
  timeBlocksBalance?: TimeBlocksBalanceCollection
  timeSheetDate: moment.Moment
  selectedCompensationBlocks?: AttendanceSidepanelCompensationBlockFragment[]
}

// TODO: Remove this component once, it gonna be replaced by CreateTimeBlockCompensationWizard
const CreateTimeBlockSupplementWizard = ({
  employeeId,
  getResponseData,
  loading,
  onCancel,
  timeBlocksBalance,
  timeSheetDate,
  selectedCompensationBlocks = [],
}: Props) => {
  const { startOn, endOnOrYesterday: endOn } = getDateRange(timeSheetDate)
  const { stringDateFrom, stringDateTo } = useAttendanceDateNavigator()

  const usingCompensationBlocks = selectedCompensationBlocks.length > 0
  const initialStep = usingCompensationBlocks ? '2' : '1'

  const form = TimeBlocksSupplementForm({
    setResponseData: getResponseData,
    employeeId: employeeId,
    startOn: timeBlocksBalance?.startOn || (usingCompensationBlocks ? stringDateFrom : startOn),
    endOn: timeBlocksBalance?.endOn || (usingCompensationBlocks ? stringDateTo : endOn),
    compensationBlockIds: selectedCompensationBlocks.map((cb) => cb.id),
  })

  const initialSelectedTimeBlocks = selectedCompensationBlocks.map((cb) =>
    UniqueTimeBlockData.fromCompensationBlock(cb)
  )

  const [selectedTimeBlocks, setSelectedTimeBlocks] =
    React.useState<UniqueTimeBlockData[]>(initialSelectedTimeBlocks)

  const [step, setStep] = React.useState<string>(initialStep)

  const { legalEntities } = useLegalEntities({
    employeesIds: [employeeId],
  })
  const legalEntityIds = legalEntities.map((l) => l.id)
  const { taxonomies: supplementsTaxonomies } = useFetchContractsTaxonomies(legalEntityIds)

  const { mappings } = useFetchCustomTimeRangeCategorySupplementMappings(
    legalEntityIds.map((id) => id)
  )

  const reimbursableIds = mappings.map((item) => item.reimbursableId)

  // remove time blocks already associated with a taxonomy from the next step
  const notMappedToTaxonomy = selectedTimeBlocks.filter(
    (timeBlock) =>
      timeBlock.timeSettingsCustomTimeRangeCategoryId &&
      !reimbursableIds.includes(timeBlock.timeSettingsCustomTimeRangeCategoryId)
  )

  const taxonomiesByLegalEntity = groupBy(
    supplementsTaxonomies.activeTaxonomies(),
    (taxonomy) => taxonomy.legalEntityId
  )

  const onSelectTimeBlock = useCallback(
    (timeBlock: UniqueTimeBlockData, checked: boolean): void => {
      let updatedTimeBlocks

      if (checked) {
        updatedTimeBlocks = [...selectedTimeBlocks, timeBlock]
      } else {
        updatedTimeBlocks = selectedTimeBlocks.filter(
          (prevTimeBlock) => prevTimeBlock.name !== timeBlock.name
        )
      }

      setSelectedTimeBlocks(updatedTimeBlocks)
      form.setValues({ ...form.values, timeBlocks: updatedTimeBlocks })
    },
    [selectedTimeBlocks, form]
  )

  const totalSteps = notMappedToTaxonomy.length + 2

  const finalStep = () => {
    setStep('1')
    track(trackingEvents.timeTracking.compensatingCategoriesFinish, {
      selectedTimeBlocks: selectedTimeBlocks.map((timeBlock) => timeBlock.name || 'balance'),
      period: form.values.period,
    })
    onCancel()
  }

  const showPayrollSupplementModal = step !== '1' && step !== String(totalSteps)

  return (
    <>
      {step === '1' && timeBlocksBalance && (
        <TimeBlocks
          onSelectTimeBlock={onSelectTimeBlock}
          onCloseModal={onCancel}
          onContinue={() => {
            track(trackingEvents.timeTracking.compensatingCategories, {
              selectedTimeBlocks: selectedTimeBlocks.map(
                (timeBlock) => timeBlock.name || 'balance'
              ),
            })
            track(trackingEvents.compensations.createCompensation, {
              source: 'timesettings_customtimerangecategory',
              selectedTimeBlocks: selectedTimeBlocks.map(
                (timeBlock) => timeBlock.name || 'balance'
              ),
            })
            setStep('2')
          }}
          disableButton={selectedTimeBlocks.length === 0}
          timeBlocksLoading={loading}
          timeBlocksBalance={timeBlocksBalance}
        />
      )}

      {showPayrollSupplementModal &&
        notMappedToTaxonomy.map((category, index) => (
          <React.Fragment key={`${category.name}-${index}`}>
            {step === String(index + 2) && (
              <PayrollSupplement
                totalCategories={notMappedToTaxonomy.length}
                currentStep={index + 1}
                onCancel={onCancel}
                category={category}
                legalEntities={legalEntities}
                taxonomiesByLegalEntity={taxonomiesByLegalEntity}
                mappings={mappings}
              />
            )}
          </React.Fragment>
        ))}

      {step === String(totalSteps) && (
        <PayrollPeriod form={form} onCancel={onCancel} onSubmit={finalStep} />
      )}
    </>
  )
}

export default CreateTimeBlockSupplementWizard
